import { useState, useEffect } from 'react';
import { HiOutlineCpuChip } from 'react-icons/hi2';
import { TbRoute } from 'react-icons/tb';
import { MdOutlineAssessment } from 'react-icons/md';
import SeoHead from '../components/SeoHead';
import anthropicChart from '../assets/Anthropic theoretical capability by occupational category.webp';

// ─────────────────────────────────────────────────────────────────────────────
// Config — update HUBSPOT_MEETING_URL before going live
// ─────────────────────────────────────────────────────────────────────────────

const HUBSPOT_MEETING_URL = 'https://meetings-eu1.hubspot.com/carlo-aice'; // TODO: replace with actual URL

// ─────────────────────────────────────────────────────────────────────────────
// Static content — edit arrays to update copy without touching JSX
// ─────────────────────────────────────────────────────────────────────────────

const DELIVERABLES_USING = [
  {
    Icon: HiOutlineCpuChip,
    title: '2 AI-assistenter',
    description:
      'Vi bygger to skræddersyede AI-assistenter til jeres specifikke processer. Inden vi pakker sammen sidder I med dem kørende.',
  },
  {
    Icon: TbRoute,
    title: 'AI køreplan',
    description:
      'Inden vi pakker sammen sidder I med en konkret liste over jeres AI-muligheder, sorteret efter hvad I kan gøre i næste uge, hvad der kræver en større investering, og hvad der simpelthen ikke er det værd lige nu.',
  },
  {
    Icon: MdOutlineAssessment,
    title: 'Ekspertvurdering',
    description:
      'En erfaren AI-konsulent gennemgår præcis hvad AI kan og ikke kan gøre for jeres virksomhed.',
  },
];

const DELIVERABLES_NEW = [
  {
    Icon: HiOutlineCpuChip,
    title: 'Jeres første AI-assistent',
    description:
      'Vi bygger jeres allerførste AI-assistent fra bunden, til en proces I selv vælger. Inden vi pakker sammen sidder I med den kørende og forstår præcis hvordan den virker.',
  },
  {
    Icon: TbRoute,
    title: 'Klar til AI',
    description:
      'Inden vi pakker sammen sidder I med en klar, prioriteret plan for hvordan I kommer i gang med AI, hvad der er lavt hængende frugt, og hvad der kræver lidt mere.',
  },
  {
    Icon: MdOutlineAssessment,
    title: 'Ekspertvurdering',
    description:
      'En erfaren AI-konsulent gennemgår præcis hvad AI kan og ikke kan gøre for jeres virksomhed, uden teknisk jargon.',
  },
];


const QUOTES = [
  {
    text: 'AICE har givet os en praksisnær og strategisk AI-indsats, hvor alle medarbejdere kunne være med. Vi har allerede set konkrete gevinster og er godt klædt på til næste skridt.',
    author: 'Mads Blaabjerg',
    industry: 'Partner og AI-ansvarlig',
  },
  {
    text: 'AICE formåede at gøre AI relevant og praksisnær for bestyrelsesarbejde. Vi fik både indsigt og værktøjer, vi kan tage med direkte ind i mødelokalet.',
    author: 'Anna Maria Hejlsberg',
    industry: 'Bestyrelsesmedlem, Bestyrelseskvinder.dk',
  },
  {
    text: 'Forløbet gav os både overblik og konkrete værktøjer til at komme i gang. Vi er godt på vej med både retning og ressourcer til at udvikle videre.',
    author: 'Partner (anonymiseret)',
    industry: 'Managementkonsulenthus',
  },
];

const PROOF_METRICS = [
  {
    figure: '765 tkr.',
    label: 'Årlig produktivitetsgevinst',
    context: 'Rådgivende ingeniørvirksomhed — opnået inden for første halve år efter kompetenceløft',
  },
  {
    figure: '100 % NPS',
    label: 'på egne kurser',
    context: 'Gennemsnit tværs af alle AICE-afholdte kursusforløb',
  },
  {
    figure: '1.000+',
    label: 'Timer sparet årligt',
    context: 'Bygherrerådgiver — registreret efter første AI-forløb',
  },
];


const STATS = [
  {
    figure: '94 %',
    label: 'af opgaver i videnstunge job er teknisk løsbare med AI i dag',
    source: 'Anthropic Labor Market Study, 2025',
  },
  {
    figure: '33 %',
    label: 'af disse opgaver udføres faktisk med AI, selv i de mest AI-egnede brancher',
    source: 'Anthropic Labor Market Study, 2025',
  },
  {
    figure: '3×',
    label: 'højere vækst hos virksomheder der implementerer AI strategisk',
    source: 'McKinsey',
  },
];

const AGENDA = [
  {
    time: 'Morgen',
    title: 'Kortlægning',
    description: 'Vi gennemgår jeres processer og identificerer de største AI-muligheder.',
  },
  {
    time: 'Formiddag',
    title: 'Prioritering',
    description: 'Sammen vælger I hvilke opgaver der bedst egner sig til AI, og i hvilken rækkefølge.',
  },
  {
    time: 'Eftermiddag',
    title: 'Bygning',
    description: 'Vi omsætter idéerne til virkelighed og bygger de første assistenter I har talt om.',
  },
  {
    time: 'Afslutning',
    title: 'Overdragelse',
    description:
      'I forlader dagen med kørende assistenter, en konkret køreplan og fuld forståelse for hvad I har bygget.',
  },
];

const ASSISTANT_CAN = [
  'Håndtere repetitive tekstopgaver',
  'Besvare spørgsmål ud fra den viden I giver den',
  'Opsummere dokumenter og møder',
  'Generere udkast til emails og rapporter',
  'Guide medarbejdere trin-for-trin',
];

const ASSISTANT_CANNOT = [
  'Tage selvstændige beslutninger',
  'Tilgå systemer den ikke har adgang til',
  'Forstå kontekst den ikke har fået',
  'Garantere 100 % korrekte svar',
  'Erstatte menneskelig vurdering',
];

const FAQS = [
  {
    q: 'Hvad bygger I af AI-assistenter?',
    a: 'Det afhænger af jeres processer. Typisk bygger vi assistenter til opgaver som mailhåndtering, dokumentopsummering, kundeservice eller mødeforberedelse, baseret på hvad I fortæller os på dagen.',
  },
  {
    q: 'Hvad kræves der af os inden dagen?',
    a: 'I skal have identificeret 2-3 processer eller opgaver, hvor I ser AI-potentiale. Derudover skal minimum 2 nøglepersoner kunne deltage. Det er alt.',
  },
  {
    q: 'Hvad sker der efter dagen?',
    a: 'I forlader dagen med kørende assistenter og en prioriteret AI-køreplan. Derfra er det op til jer, men vi er ikke forsvundet. Mange vælger at booke et opfølgende forløb, og vi er altid tilgængelige for spørgsmål. Målet er at I er selvkørende, ikke afhængige af os.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// LeadForm — reusable lead capture form (rendered in both hero and pricing)
// ─────────────────────────────────────────────────────────────────────────────

const LeadForm = ({ ctaLabel = 'Ja tak, kontakt mig' }) => {
  const [fields, setFields] = useState({ navn: '', virksomhed: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/api/ai-dag-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      if (!res.ok) throw new Error('Server error');
      setSubmitted(true);
    } catch (err) {
      console.error('[AI-dag signup]', err);
      alert('Noget gik galt. Prøv igen eller kontakt os direkte.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={successBox}>
        <p style={{ margin: 0, fontWeight: 700, color: '#2A0D05', fontSize: '1.05rem' }}>Tak!</p>
        <p style={{ margin: '0.3rem 0 0', color: '#5C3A32', fontSize: '0.95rem' }}>
          Vi ringer dig op inden for 1 arbejdsdag.
        </p>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} style={formEl}>
        <input
          type="text"
          name="navn"
          placeholder="Navn"
          required
          value={fields.navn}
          onChange={handleChange}
          style={inputEl}
        />
        <input
          type="text"
          name="virksomhed"
          placeholder="Virksomhed"
          required
          value={fields.virksomhed}
          onChange={handleChange}
          style={inputEl}
        />
        <input
          type="email"
          name="email"
          placeholder="Email"
          required
          value={fields.email}
          onChange={handleChange}
          style={inputEl}
        />
        <button
          type="submit"
          disabled={loading}
          style={{ ...ctaBtn, opacity: loading ? 0.7 : 1 }}
          onMouseOver={(e) => { if (!loading) e.currentTarget.style.opacity = '0.85'; }}
          onMouseOut={(e) => { e.currentTarget.style.opacity = loading ? '0.7' : '1'; }}
        >
          {loading ? 'Sender…' : ctaLabel}
        </button>
        <p style={callbackText}>Vi ringer inden for 1 arbejdsdag</p>
      </form>
      <p style={altCtaStyle}>
        Foretrækker du at booke direkte?{' '}
        <a href={HUBSPOT_MEETING_URL} target="_blank" rel="noopener noreferrer" style={altCtaLinkStyle}>
          Book et møde →
        </a>
      </p>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AIDag — main campaign landing page
// Route: /ai-dag
// ─────────────────────────────────────────────────────────────────────────────

const AIDag = () => {
  const [isNarrow, setIsNarrow] = useState(false);
  const [profile, setProfile] = useState('using'); // 'using' | 'new'

  useEffect(() => {
    const check = () => setIsNarrow(window.innerWidth < 900);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const deliverables = profile === 'using' ? DELIVERABLES_USING : DELIVERABLES_NEW;

  return (
    <div style={pageWrap}>
      <SeoHead
        title="AI Discovery Day — AICE | 8.000 kr"
        description="Få klarhed over hvad AI kan gøre for jeres virksomhed på én dag. Vi bygger 2 skræddersyede AI-assistenter og leverer en konkret AI-køreplan. Normalt 10.000 kr — nu kun 8.000 kr."
        canonicalPath="/ai-dag"
      />

      {/* ────────────────────────────────────────────────────────────────────
          1. HERO — everything above the fold on desktop
          Two-column layout: headline left, form card right.
      ──────────────────────────────────────────────────────────────────── */}
      <section style={heroSection}>
        <div
          style={{
            ...heroInner,
            gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr',
            display: isNarrow ? 'flex' : 'grid',
            flexDirection: isNarrow ? 'column' : undefined,
          }}
        >
          {/* Left: headline + subheadline */}
          <div style={heroTextBlock}>
            <p style={eyebrow}>AI Day</p>
            <h1 style={{ ...h1Style, fontSize: isNarrow ? '1.85rem' : h1Style.fontSize }}>
              En dag. To kørende AI-assistenter. En konkret AI-plan.
            </h1>
            <p style={heroSubStyle}>
              En AI-ekspert gennemgår jeres processer, identificerer jeres største muligheder og
              bygger skræddersyede AI-assistenter til jer.
            </p>
            {/* Price badge */}
            <div style={heroPriceBadge}>
              <span style={heroPriceOld}>10.000 kr</span>
              <span style={heroPriceArrow}>→</span>
              <span style={heroPriceNew}>8.000 kr</span>
              <span style={heroPriceSaving}>Spar 20 %</span>
            </div>
          </div>

          {/* Right: lead form inside a card */}
          <div style={{ ...formCard, alignSelf: isNarrow ? 'stretch' : 'end' }}>
            <LeadForm />
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          2. STATS — "Hvad siger tallene?"
      ──────────────────────────────────────────────────────────────────── */}
      <section style={{ ...sectionPad, backgroundColor: '#FFF0EC', borderBottom: '1px solid #F3D9D2' }}>
        <div style={sectionInner}>
          <p style={eyebrow}>Hvad siger tallene?</p>
          <h2 style={h2Style}>AI er ikke fremtid. Det er nu.</h2>
          <div
            style={{
              ...cardsGrid,
              gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3, 1fr)',
              marginTop: '0.5rem',
            }}
          >
            {STATS.map((s) => (
              <div key={s.figure} style={statCard}>
                <span style={statFigure}>{s.figure}</span>
                <p style={statLabel}>{s.label}</p>
                <p style={statSource}>{s.source}</p>
              </div>
            ))}
          </div>
          <div style={chartWrap}>
            <img
              src={anthropicChart}
              alt="Theoretical AI capability vs observed usage by occupational category"
              style={chartImg}
            />
            <p style={chartCaption}>
              Teoretisk AI-dækning (blå) vs. faktisk brug (rød) på tværs af brancher.
              Gabet er det uudnyttede potentiale. Kilde: Anthropic, 2025
            </p>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          3. SOCIAL PROOF — metric cards + featured quote + supporting quotes
      ──────────────────────────────────────────────────────────────────── */}
      <section style={{ ...sectionPad, backgroundColor: '#fff', borderBottom: '1px solid #F3D9D2', paddingBottom: '5rem' }}>
        <div style={sectionInner}>
          <p style={eyebrow}>Det siger kunderne</p>
          <h2 style={h2Style}>Resultater vi kan stå inde for</h2>

          {/* Metric cards — standalone, accent-top design */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3, 1fr)',
            gap: '1.25rem',
            marginTop: '0.25rem',
          }}>
            {PROOF_METRICS.map((m) => (
              <div key={m.figure} style={proofMetricCard}>
                <span style={proofMetricFigure}>{m.figure}</span>
                <p style={proofMetricLabel}>{m.label}</p>
                <p style={proofMetricContext}>{m.context}</p>
              </div>
            ))}
          </div>

          {/* Quotes — 3 columns */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3, 1fr)',
            gap: '1.25rem',
          }}>
            {QUOTES.map((q, i) => (
              <div key={i} style={quoteCard}>
                <p style={quoteText}>"{q.text}"</p>
                <div>
                  <p style={quoteAuthor}>{q.author}</p>
                  <p style={quoteIndustry}>{q.industry}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          4. PROFILE TOGGLE — affects sections 5 and 7
      ──────────────────────────────────────────────────────────────────── */}
      <section style={{ ...sectionPad, paddingBottom: '0' }}>
        <div style={{ ...sectionInner, alignItems: 'center', maxWidth: '700px' }}>
          <p style={eyebrow}>Tilpas til jer</p>
          <h2 style={{ ...h2Style, textAlign: 'center' }}>Hvor er I henne med AI?</h2>
          <div style={sliderTrack}>
            <div style={{
              ...sliderThumb,
              left: profile === 'using' ? '4px' : '50%',
              right: profile === 'new' ? '4px' : '50%',
            }} />
            <button
              type="button"
              style={{ ...sliderLabel, color: profile === 'using' ? '#fff' : '#C03626' }}
              onClick={() => setProfile('using')}
            >
              Vi bruger allerede AI
            </button>
            <button
              type="button"
              style={{ ...sliderLabel, color: profile === 'new' ? '#fff' : '#C03626' }}
              onClick={() => setProfile('new')}
            >
              Vi er ikke startet endnu
            </button>
          </div>
          <p style={toggleHintStyle}>Indholdet nedenfor tilpasser sig jeres udgangspunkt.</p>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          5. WHAT YOU GET — three deliverable cards (toggle-affected)
      ──────────────────────────────────────────────────────────────────── */}
      <section style={sectionPad}>
        <div style={sectionInner}>
          <p style={eyebrow}>Hvad I opnår</p>
          <h2 style={h2Style}>Jeres udbytte fra dagen</h2>
          <div
            style={{
              ...cardsGrid,
              gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3, 1fr)',
            }}
          >
            {deliverables.map(({ Icon, title, description }) => (
              <div key={title} style={delivCard}>
                <Icon style={cardIconStyle} />
                <h3 style={cardTitleStyle}>{title}</h3>
                <p style={cardBodyStyle}>{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          6. AGENDA — "Sådan ser dagen ud"
      ──────────────────────────────────────────────────────────────────── */}
      <section style={{ ...sectionPad, backgroundColor: '#FFF0EC' }}>
        <div style={sectionInner}>
          <p style={eyebrow}>Program</p>
          <h2 style={h2Style}>Sådan ser dagen ud</h2>
          <div
            style={{
              ...cardsGrid,
              gridTemplateColumns: isNarrow ? '1fr' : 'repeat(4, 1fr)',
              marginTop: '0.75rem',
            }}
          >
            {AGENDA.map((step, i) => (
              <div key={step.title} style={agendaStep}>
                <span style={agendaNumber}>{i + 1}</span>
                <p style={agendaTime}>{step.time}</p>
                <h3 style={agendaTitle}>{step.title}</h3>
                <p style={agendaDesc}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          7. WHAT IS AN AI ASSISTANT — 2-column can / can't
      ──────────────────────────────────────────────────────────────────── */}
      <section style={sectionPad}>
        <div style={{ ...sectionInner, maxWidth: '900px' }}>
          <p style={eyebrow}>Forståelse</p>
          <h2 style={h2Style}>Hvad er en AI-assistent?</h2>
          <p style={{ margin: '0', color: '#4A2E25', lineHeight: 1.65, fontSize: '1rem' }}>
            En AI-assistent er et program der er trænet til at løse specifikke opgaver for jer,
            baseret på jeres sprog, jeres processer og jeres behov.
          </p>
          <div
            style={{
              display: isNarrow ? 'flex' : 'grid',
              flexDirection: isNarrow ? 'column' : undefined,
              gridTemplateColumns: '1fr 1fr',
              gap: '1.5rem',
              marginTop: '0.5rem',
            }}
          >
            <div style={canCard}>
              <p style={canHeader}>Den kan</p>
              <ul style={canList}>
                {ASSISTANT_CAN.map((item) => (
                  <li key={item} style={canItem}>
                    <span style={{ color: '#C03626', fontWeight: 700, marginRight: '0.6rem' }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ ...canCard, background: '#FFF9F8', borderColor: '#F3D9D2' }}>
              <p style={{ ...canHeader, color: '#9A7070' }}>Den kan ikke</p>
              <ul style={canList}>
                {ASSISTANT_CANNOT.map((item) => (
                  <li key={item} style={canItem}>
                    <span style={{ color: '#C8A09A', fontWeight: 700, marginRight: '0.6rem' }}>✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

{/* ────────────────────────────────────────────────────────────────────
          9. PRICING — centered, with repeated lead form
      ──────────────────────────────────────────────────────────────────── */}
      <section style={sectionPad}>
        <div style={{ ...sectionInner, maxWidth: '580px', alignItems: 'center', textAlign: 'center' }}>
          <p style={eyebrow}>Pris</p>
          <h2 style={h2Style}>Én dag. To AI-assistenter. En konkret køreplan.</h2>
          <div style={priceBlockStyle}>
            <span style={oldPriceStyle}>10.000 kr</span>
            <span style={newPriceStyle}>8.000 kr</span>
          </div>
          <p style={halfPriceExplainStyle}>
            Halv pris, fordi vi er i gang med at opbygge vores portefølje af AI Day-cases. I betaler
            med jeres tillid, vi betaler med vores bedste arbejde.
          </p>
          {/* Second instance of the lead form */}
          <div style={{ width: '100%', textAlign: 'left' }}>
            <LeadForm ctaLabel="Reservér jeres plads" />
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          10. FAQ
      ──────────────────────────────────────────────────────────────────── */}
      <section style={{ ...sectionPad, backgroundColor: '#FFF0EC' }}>
        <div style={{ ...sectionInner, maxWidth: '700px' }}>
          <p style={eyebrow}>Spørgsmål & svar</p>
          <h2 style={h2Style}>Hyppige spørgsmål</h2>
          <p style={faqIntroStyle}>Hvad vil I vide?</p>
          <div style={faqListStyle}>
            {FAQS.map((faq) => (
              <div key={faq.q} style={faqItemStyle}>
                <p style={faqQStyle}>{faq.q}</p>
                <p style={faqAStyle}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIDag;

// ─────────────────────────────────────────────────────────────────────────────
// Style objects — grouped by section for easy editing
// ─────────────────────────────────────────────────────────────────────────────

/* ── Page ── */
const pageWrap = {
  backgroundColor: '#FFF9F8',
  minHeight: '100vh',
};

/* ── Hero ── */
const heroSection = {
  padding: '3.5rem clamp(1.5rem, 6vw, 5rem) 4rem',
  borderBottom: '1px solid #F3D9D2',
  backgroundColor: '#FFF9F8',
};

const heroInner = {
  maxWidth: '1100px',
  margin: '0 auto',
  gap: '3rem',
  alignItems: 'start',
};

const heroTextBlock = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.1rem',
  justifyContent: 'center',
};

const h1Style = {
  margin: 0,
  fontSize: '2.65rem',
  fontWeight: 700,
  color: '#2A0D05',
  lineHeight: 1.15,
};

const heroSubStyle = {
  margin: 0,
  fontSize: '1.1rem',
  color: '#4A2E25',
  lineHeight: 1.7,
};

const formCard = {
  background: '#fff',
  border: '1px solid #F3D9D2',
  borderRadius: '18px',
  padding: '1.75rem 1.5rem',
  boxShadow: '0 8px 28px rgba(192, 54, 38, 0.09)',
  flexShrink: 0,
  alignSelf: 'end',
};

/* ── Shared section layout ── */
const sectionPad = {
  padding: '70px clamp(1.5rem, 5vw, 4rem)',
};

const sectionInner = {
  maxWidth: '1100px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

/* ── Typography ── */
const eyebrow = {
  textTransform: 'uppercase',
  letterSpacing: '0.22em',
  color: '#C03626',
  fontSize: '0.82rem',
  fontWeight: 600,
  margin: 0,
};

const h2Style = {
  margin: 0,
  fontSize: '2rem',
  fontWeight: 700,
  color: '#2A0D05',
  lineHeight: 1.2,
};

/* ── Deliverable cards ── */
const cardsGrid = {
  display: 'grid',
  gap: '1.5rem',
  marginTop: '0.75rem',
};

const delivCard = {
  background: '#FFF0EC',
  border: '1px solid #F3D9D2',
  borderRadius: '16px',
  padding: '1.75rem 1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const cardIconStyle = {
  fontSize: '2rem',
  color: '#C03626',
  flexShrink: 0,
};

const cardTitleStyle = {
  margin: 0,
  fontSize: '1.2rem',
  fontWeight: 700,
  color: '#2A0D05',
};

const cardBodyStyle = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.6,
  fontSize: '0.97rem',
};

/* ── Social proof ── */
const proofMetricCard = {
  background: '#fff',
  border: '1px solid #F3D9D2',
  borderTop: '3px solid #C03626',
  borderRadius: '14px',
  padding: '1.5rem 1.5rem 1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.3rem',
};

const proofMetricFigure = {
  fontSize: '2.6rem',
  fontWeight: 800,
  color: '#C03626',
  lineHeight: 1,
};

const proofMetricLabel = {
  margin: 0,
  color: '#2A0D05',
  fontSize: '0.95rem',
  fontWeight: 600,
  lineHeight: 1.4,
};

const proofMetricContext = {
  margin: 0,
  color: '#9A8A87',
  fontSize: '0.8rem',
  lineHeight: 1.4,
};



const quoteCard = {
  background: '#FFF0EC',
  border: '1px solid #F3D9D2',
  borderRadius: '16px',
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const quoteText = {
  margin: 0,
  color: '#2A0D05',
  fontSize: '0.97rem',
  lineHeight: 1.65,
  fontStyle: 'italic',
  flexGrow: 1,
};

const quoteAuthor = {
  margin: 0,
  fontWeight: 700,
  fontSize: '0.88rem',
  color: '#2A0D05',
};

const quoteIndustry = {
  margin: 0,
  fontSize: '0.82rem',
  color: '#9A8A87',
};

/* ── Stats ── */
const statCard = {
  background: '#fff',
  border: '1px solid #F3D9D2',
  borderRadius: '16px',
  padding: '1.75rem 1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const statFigure = {
  fontSize: '3rem',
  fontWeight: 800,
  color: '#C03626',
  lineHeight: 1,
};

const statLabel = {
  margin: 0,
  color: '#2A0D05',
  lineHeight: 1.5,
  fontSize: '1rem',
};

const statSource = {
  margin: 0,
  color: '#9A8A87',
  fontSize: '0.8rem',
  fontStyle: 'italic',
};

const chartWrap = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.75rem',
  marginTop: '1rem',
};

const chartImg = {
  width: '100%',
  maxWidth: '560px',
  borderRadius: '16px',
  border: '1px solid #F3D9D2',
};

const chartCaption = {
  margin: 0,
  textAlign: 'center',
  color: '#9A8A87',
  fontSize: '0.82rem',
  fontStyle: 'italic',
  maxWidth: '560px',
};

/* ── Profile slider toggle ── */
const sliderTrack = {
  position: 'relative',
  display: 'inline-flex',
  background: '#FFF0EC',
  border: '1.5px solid #F3D9D2',
  borderRadius: '999px',
  padding: '4px',
  marginTop: '0.5rem',
};

const toggleHintStyle = {
  margin: '0.75rem 0 0',
  color: '#9A8A87',
  fontSize: '0.88rem',
  textAlign: 'center',
};

const sliderThumb = {
  position: 'absolute',
  top: '4px',
  bottom: '4px',
  background: '#C03626',
  borderRadius: '999px',
  transition: 'left 0.25s ease, right 0.25s ease',
};

const sliderLabel = {
  position: 'relative',
  zIndex: 1,
  flex: 1,
  padding: '0.65rem 1.4rem',
  fontWeight: 600,
  fontSize: '0.95rem',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  borderRadius: '999px',
  textAlign: 'center',
  transition: 'color 0.25s ease',
  whiteSpace: 'nowrap',
};

/* ── Agenda ── */
const agendaStep = {
  background: '#fff',
  border: '1px solid #F3D9D2',
  borderRadius: '16px',
  padding: '1.5rem 1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const agendaNumber = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: '#C03626',
  color: '#fff',
  fontWeight: 800,
  fontSize: '0.9rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const agendaTime = {
  margin: 0,
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#C03626',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
};

const agendaTitle = {
  margin: 0,
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#2A0D05',
};

const agendaDesc = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.55,
  fontSize: '0.93rem',
};

/* ── AI-assistent can/can't ── */
const canCard = {
  background: '#FFF0EC',
  border: '1px solid #F3D9D2',
  borderRadius: '16px',
  padding: '1.5rem',
};

const canHeader = {
  margin: '0 0 1rem',
  fontWeight: 700,
  fontSize: '1.05rem',
  color: '#2A0D05',
};

const canList = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.7rem',
};

const canItem = {
  color: '#2A0D05',
  fontSize: '0.97rem',
  lineHeight: 1.5,
  display: 'flex',
  alignItems: 'flex-start',
};


/* ── Pricing ── */
const priceBlockStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '1.25rem',
  flexWrap: 'wrap',
  margin: '0.25rem 0 0',
};

const oldPriceStyle = {
  fontSize: '1.5rem',
  color: '#B0A0A0',
  textDecoration: 'line-through',
};

const newPriceStyle = {
  fontSize: '3.25rem',
  fontWeight: 800,
  color: '#C03626',
  lineHeight: 1,
};

const halfPriceExplainStyle = {
  margin: '0.25rem 0 0',
  color: '#7A5550',
  fontSize: '0.93rem',
  lineHeight: 1.6,
  fontStyle: 'italic',
};

const priceLimitStyle = {
  color: '#5C3A32',
  fontWeight: 500,
  fontSize: '1rem',
  margin: '0.25rem 0 1rem',
};

/* ── FAQ ── */
const faqIntroStyle = {
  margin: '0.25rem 0 0',
  color: '#2A0D05',
  fontWeight: 600,
  fontSize: '1.1rem',
};

const faqListStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
  marginTop: '0.25rem',
};

const faqItemStyle = {
  borderTop: '1px solid #F3D9D2',
  padding: '1.5rem 0',
};

const faqQStyle = {
  margin: '0 0 0.6rem',
  fontWeight: 700,
  color: '#2A0D05',
  fontSize: '1.05rem',
};

const faqAStyle = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.65,
};

/* ── LeadForm ── */
const formEl = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const inputEl = {
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  border: '1px solid #F3D9D2',
  fontSize: '1rem',
  outline: 'none',
  background: '#fff',
  color: '#2A0D05',
  width: '100%',
  boxSizing: 'border-box',
};

const ctaBtn = {
  backgroundColor: '#C03626',
  color: '#fff',
  padding: '0.85rem 1.5rem',
  borderRadius: '8px',
  border: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  transition: 'opacity 0.2s ease',
  marginTop: '0.25rem',
  width: '100%',
};

const callbackText = {
  margin: 0,
  textAlign: 'center',
  color: '#9A8A87',
  fontSize: '0.85rem',
};

const altCtaStyle = {
  margin: '0.75rem 0 0',
  textAlign: 'center',
  color: '#7A5550',
  fontSize: '0.9rem',
};

const altCtaLinkStyle = {
  color: '#C03626',
  fontWeight: 600,
  textDecoration: 'none',
};

const successBox = {
  background: '#FFF0EC',
  border: '1px solid #F3D9D2',
  borderRadius: '12px',
  padding: '1.5rem',
  textAlign: 'center',
};

/* ── Hero price badge ── */
const heroPriceBadge = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.75rem',
  background: '#fff',
  border: '1.5px solid #F3D9D2',
  borderRadius: '12px',
  padding: '0.75rem 1.25rem',
  boxShadow: '0 4px 16px rgba(232, 98, 42, 0.12)',
  flexWrap: 'wrap',
  marginTop: '0.25rem',
};

const heroPriceOld = {
  fontSize: '1.25rem',
  color: '#B0A0A0',
  textDecoration: 'line-through',
  fontWeight: 500,
};

const heroPriceArrow = {
  fontSize: '1rem',
  color: '#C8B0A8',
};

const heroPriceNew = {
  fontSize: '2rem',
  fontWeight: 800,
  color: '#C03626',
  lineHeight: 1,
};

const heroPriceSaving = {
  fontSize: '0.8rem',
  fontWeight: 700,
  color: '#fff',
  background: '#C03626',
  borderRadius: '999px',
  padding: '0.2rem 0.65rem',
  letterSpacing: '0.03em',
};
